/**
 * A link to a place on a page ("#memo"): once the page has the element, take the player to it. The router's location leaves the hash out, so this listens to the
 * navigation itself; the element may arrive late (a section that loads), so it waits for it with a MutationObserver, for a bounded time, and stops when it is found.
 */
import { useEffect } from "react";
import { reveal } from "../decisions/reveal";

const WAIT_MS = 15_000;

export function useScrollToHash(): void {
  useEffect(() => {
    let stop: (() => void) | null = null;
    const seek = (): void => {
      stop?.();
      stop = null;
      const id = decodeURIComponent(window.location.hash.slice(1));
      if (id === "") return;
      const find = (): boolean => {
        const el = document.getElementById(id);
        if (el === null) return false;
        if (!el.hasAttribute("tabindex")) el.setAttribute("tabindex", "-1");
        reveal(el);
        return true;
      };
      if (find()) return;
      const observer = new MutationObserver(() => { if (find()) { observer.disconnect(); clearTimeout(timer); } });
      const timer = setTimeout(() => observer.disconnect(), WAIT_MS);
      observer.observe(document.body, { childList: true, subtree: true });
      stop = () => { observer.disconnect(); clearTimeout(timer); };
    };
    seek();
    window.addEventListener("gx:navigate", seek);
    window.addEventListener("popstate", seek);
    window.addEventListener("hashchange", seek);
    return () => { stop?.(); window.removeEventListener("gx:navigate", seek); window.removeEventListener("popstate", seek); window.removeEventListener("hashchange", seek); };
  }, []);
}
